import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import useHashFile from '../hooks/useHashFile';
import { useDatabase } from '../context/DatabaseContext';
import { useActivity } from '../context/ActivityContext';
import { authenticateWithML } from '../services/mlAuthenticationClient';

const CheckPage = () => {
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [verificationResult, setVerificationResult] = useState(null);
  const [isChecking, setIsChecking] = useState(false);
  const [checkingStep, setCheckingStep] = useState('');
  const [showMLOption, setShowMLOption] = useState(false);
  const [isRunningML, setIsRunningML] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  
  const { hashFile, isHashing } = useHashFile();
  const { findCertificateByHash } = useDatabase();
  const { log } = useActivity();

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleVerify = async () => {
    if (!file) {
      alert('Please select a file to verify.');
      return;
    }

    try {
      setIsChecking(true);
      setVerificationResult(null);
      setShowMLOption(false);
      setCurrentStep(1);
      
      // Step 1: Database Check
      setCheckingStep('📝 Computing certificate hash...');
      
      // Generate SHA256 hash of the uploaded file
      const fileHash = (await hashFile(file))?.toString().trim().toLowerCase();
      
      setCheckingStep('🔍 Step 1: Checking institutional database...');
      await new Promise(resolve => setTimeout(resolve, 1500)); // UX delay
      
      // Search for the hash in our database
      const foundCertificate = findCertificateByHash(fileHash);
      
      if (foundCertificate) {
        // Certificate found in database - it's valid
        setVerificationResult({
          found: true,
          method: 'database',
          fileHash,
          certificate: foundCertificate,
          timestamp: new Date().toISOString()
        });
        setCurrentStep(2);
        setCheckingStep('✅ Certificate verified from institutional database');
        
        log({ 
          action: 'verify', 
          fileHash, 
          institution: foundCertificate.institution, 
          cid: foundCertificate.cid, 
          flags: ['db_found'] 
        });
      } else {
        // Certificate not found - show option for ML verification
        setVerificationResult({
          found: false,
          method: 'database_not_found',
          fileHash,
          timestamp: new Date().toISOString()
        });
        setCurrentStep(2);
        setCheckingStep('❌ Certificate hash not found in database');
        setShowMLOption(true);
        
        log({ 
          action: 'verify', 
          fileHash, 
          institution: 'unknown', 
          flags: ['db_not_found'] 
        });
      }
      
      setIsChecking(false);
      
    } catch (error) {
      console.error('Verification failed:', error);
      setVerificationResult({
        found: false,
        method: 'error',
        fileHash: null,
        error: error.message,
        timestamp: new Date().toISOString()
      });
      setCheckingStep(`❌ Verification failed: ${error.message}`);
      setIsChecking(false);
    }
  };

  const handleMLAuthentication = async () => {
    if (!file) return;

    setIsRunningML(true);
    setShowMLOption(false);
    setCurrentStep(3);

    try {
      setCheckingStep('🤖 Step 2: Running AI authenticity analysis...');
      
      // Test model availability first
      setCheckingStep('🔧 Initializing AI model...');
      
      // Run ML prediction
      setCheckingStep('🧠 Analyzing certificate patterns with AI...');
      const mlAuthentication = await authenticateWithML(file);
      
      console.log('🎯 ML Authentication Result:', mlAuthentication);

      // Update verification result with ML data
      setVerificationResult(prev => ({
        ...prev,
        method: 'ml_authentication',
        authentication: mlAuthentication,
        isValid: mlAuthentication.isAuthentic
      }));

      setCheckingStep(
        mlAuthentication.isAuthentic 
          ? `✅ AI Analysis Complete: Certificate appears authentic`
          : `⚠️ AI Analysis Complete: Certificate appears suspicious`
      );

      log({ 
        action: 'verify', 
        fileHash: verificationResult?.fileHash, 
        institution: 'unknown', 
        flags: ['db_not_found', 'ml_checked', mlAuthentication.isAuthentic ? 'ml_authentic' : 'ml_suspicious'],
        mlConfidence: mlAuthentication.confidence
      });

    } catch (error) {
      console.error('❌ ML Authentication failed:', error);
      setVerificationResult(prev => ({
        ...prev,
        method: 'ml_error',
        mlError: error.message
      }));
      setCheckingStep(`❌ AI analysis failed: ${error.message}`);
    } finally {
      setIsRunningML(false);
    }
  };

  const renderResults = () => {
    if (!verificationResult) return null;

    // Database Match Result
    if (verificationResult.found && verificationResult.method === 'database') {
      const cert = verificationResult.certificate;
      return (
        <div className="bg-green-50 border border-green-200 rounded-lg p-6">
          <div className="flex items-center mb-4">
            <div className="flex-shrink-0">
              <svg className="h-8 w-8 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-lg font-medium text-green-800">✅ Certificate Verified</h3>
              <p className="text-sm text-green-600">Found in institutional database</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 text-sm">
            <div className="space-y-3">
              <div className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
                <span className="font-semibold text-gray-700">Student:</span>
                <div className="text-gray-600 mt-1">{cert.fileName || cert.file_name || 'N/A'}</div>
              </div>
              <div className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
                <span className="font-semibold text-gray-700">Institution:</span>
                <div className="text-gray-600 mt-1">{cert.institution || 'N/A'}</div>
              </div>
              <div className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
                <span className="font-semibold text-gray-700">Upload Date:</span>
                <div className="text-gray-600 mt-1">{cert.uploadDate || cert.upload_date || 'N/A'}</div>
              </div>
            </div>
            <div className="space-y-3">
              <div className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
                <span className="font-semibold text-gray-700">Certificate Hash:</span>
                <div className="bg-gray-50 px-3 py-2 rounded mt-1 font-mono text-xs break-all border">
                  {verificationResult.fileHash?.substring(0, 32)}...
                </div>
              </div>
              <div className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
                <span className="font-semibold text-gray-700">IPFS:</span>
                <div className="mt-1">
                  {cert.ipfsUrl || cert.ipfs_url ? (
                    <a 
                      href={cert.ipfsUrl || cert.ipfs_url} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="inline-flex items-center text-orange-600 hover:text-orange-700 underline font-medium"
                    >
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                      View on IPFS
                    </a>
                  ) : (
                    <span className="text-gray-500">N/A</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-4 p-4 bg-green-100 rounded-lg border border-green-200">
            <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold bg-green-600 text-white shadow-sm">
              ✓ DATABASE VERIFIED
            </span>
          </div>
        </div>
      );
    }

    // Certificate Not Found in Database
    if (!verificationResult.found && verificationResult.method === 'database_not_found') {
      return (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-6">
          <div className="flex items-center mb-4">
            <div className="flex-shrink-0">
              <svg className="h-8 w-8 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-lg font-medium text-orange-800">⚠️ Certificate Hash Not Found</h3>
              <p className="text-sm text-orange-600">This certificate is not in our institutional database</p>
            </div>
          </div>

          <div className="mb-4">
            <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
              <h4 className="font-semibold text-orange-800 mb-3">📋 Certificate Analysis</h4>
              <div className="space-y-2">
                <div className="bg-white p-3 rounded border border-orange-100">
                  <span className="font-semibold text-gray-700">Hash:</span>
                  <div className="bg-gray-50 px-3 py-2 rounded mt-1 font-mono text-xs break-all border">
                    {verificationResult.fileHash?.substring(0, 48)}...
                  </div>
                </div>
                <div className="bg-white p-3 rounded border border-orange-100">
                  <span className="font-semibold text-gray-700">Status:</span>
                  <div className="text-gray-600 mt-1">Not found in database</div>
                </div>
                <div className="bg-white p-3 rounded border border-orange-100">
                  <span className="font-semibold text-gray-700">Checked:</span>
                  <div className="text-gray-600 mt-1">{new Date(verificationResult.timestamp).toLocaleString()}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <h4 className="font-semibold text-red-800 mb-3">🚨 Possible Reasons</h4>
            <div className="space-y-2">
              <div className="bg-white p-3 rounded border border-red-100">
                <div className="flex items-start">
                  <span className="text-red-600 font-semibold mr-2">•</span>
                  <div>
                    <span className="font-semibold text-red-700">Not Uploaded:</span>
                    <div className="text-red-600 text-sm mt-1">Certificate may not have been uploaded by the institution</div>
                  </div>
                </div>
              </div>
              <div className="bg-white p-3 rounded border border-red-100">
                <div className="flex items-start">
                  <span className="text-red-600 font-semibold mr-2">•</span>
                  <div>
                    <span className="font-semibold text-red-700">Forged Document:</span>
                    <div className="text-red-600 text-sm mt-1">Certificate might be fraudulent or tampered with</div>
                  </div>
                </div>
              </div>
              <div className="bg-white p-3 rounded border border-red-100">
                <div className="flex items-start">
                  <span className="text-red-600 font-semibold mr-2">•</span>
                  <div>
                    <span className="font-semibold text-red-700">Different Institution:</span>
                    <div className="text-red-600 text-sm mt-1">Certificate from an institution not in our database</div>
                  </div>
                </div>
              </div>
              <div className="bg-white p-3 rounded border border-red-100">
                <div className="flex items-start">
                  <span className="text-red-600 font-semibold mr-2">•</span>
                  <div>
                    <span className="font-semibold text-red-700">Recent Upload:</span>
                    <div className="text-red-600 text-sm mt-1">May not have been synced to our verification database yet</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {showMLOption && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h4 className="font-semibold text-blue-800 mb-3">🤖 AI Authenticity Analysis Available</h4>
              <div className="bg-white p-4 rounded-lg border border-blue-100 mb-4">
                <p className="text-blue-700 text-sm leading-relaxed">
                  Run our AI model to analyze certificate patterns and detect potential forgeries.
                  This analysis examines visual elements, text patterns, and document structure.
                </p>
              </div>
              <button
                onClick={handleMLAuthentication}
                disabled={isRunningML}
                className="btn-navy w-max font-bold rounded-md focus:ring-4 text-sm px-4 md:px-6 py-2 lg:py-2.5 border border-[#09314D] bg-[#09314D] text-white hover:bg-white hover:text-[#09314D] hidden lg:block"
              >
                {isRunningML ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0a12 12 0 100 24v-4a8 8 0 010-16z"></path>
                    </svg>
                    <span>Running AI Analysis...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                    <span>🤖 Run AI Authenticity Test</span>
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      );
    }

    // ML Authentication Result
    if (verificationResult.method === 'ml_authentication' && verificationResult.authentication) {
      const auth = verificationResult.authentication;
      const isAuthentic = auth.isAuthentic;
      const bgColor = isAuthentic ? 'bg-blue-50 border-blue-200' : 'bg-red-50 border-red-200';
      const iconColor = isAuthentic ? 'text-blue-500' : 'text-red-500';
      const textColor = isAuthentic ? 'text-blue-800' : 'text-red-800';

      return (
        <div className={`${bgColor} border rounded-lg p-6`}>
          <div className="flex items-center mb-4">
            <div className="flex-shrink-0">
              {isAuthentic ? (
                <svg className={`h-8 w-8 ${iconColor}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              ) : (
                <svg className={`h-8 w-8 ${iconColor}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              )}
            </div>
            <div className="ml-3">
              <h3 className={`text-lg font-medium ${textColor}`}>
                {isAuthentic ? '🤖 AI Analysis: Likely Authentic' : '🚨 AI Analysis: Suspicious Document'}
              </h3>
              <p className={`text-sm ${textColor.replace('800', '600')}`}>
                AI model analyzed visual patterns and document structure
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 text-sm mb-6">
            <div className="space-y-3">
              <div className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
                <span className="font-semibold text-gray-700">AI Prediction:</span>
                <div className="text-gray-600 mt-1">{auth.prediction}</div>
              </div>
              <div className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
                <span className="font-semibold text-gray-700">Confidence Level:</span>
                <div className="text-gray-600 mt-1">{auth.confidenceStatus}</div>
              </div>
              <div className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
                <span className="font-semibold text-gray-700">Model Used:</span>
                <div className="text-gray-600 mt-1">{auth.details?.modelType || 'SIH MobileNetV2'}</div>
              </div>
            </div>
            <div className="space-y-3">
              <div className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
                <span className="font-semibold text-gray-700">Authentic Probability:</span>
                <div className="text-gray-600 mt-1 font-mono">{auth.realProbability}%</div>
              </div>
              <div className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
                <span className="font-semibold text-gray-700">Suspicious Probability:</span>
                <div className="text-gray-600 mt-1 font-mono">{auth.fakeProbability}%</div>
              </div>
              <div className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
                <span className="font-semibold text-gray-700">Overall Confidence:</span>
                <div className="text-gray-600 mt-1 font-mono">{auth.confidence}%</div>
              </div>
            </div>
          </div>

          {/* Confidence Progress Bar */}
          <div className="mb-6">
            <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
              <div className="flex justify-between text-sm font-semibold mb-2">
                <span className="text-gray-700">AI Confidence Level</span>
                <span className="text-gray-600">{auth.confidence}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-4 border">
                <div 
                  className={`h-4 rounded-full transition-all duration-500 ${
                    auth.confidence > 85 ? 'bg-gradient-to-r from-green-500 to-green-600' : 
                    auth.confidence > 65 ? 'bg-gradient-to-r from-yellow-500 to-yellow-600' : 'bg-gradient-to-r from-red-500 to-red-600'
                  }`}
                  style={{ width: `${auth.confidence}%` }}
                ></div>
              </div>
            </div>
          </div>

          {/* Warning for non-database verified certificates */}
          <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-start">
              <svg className="h-6 w-6 text-yellow-500 mt-0.5 mr-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <div className="text-sm text-yellow-700">
                <p className="font-semibold mb-2">⚠️ Important Notice</p>
                <div className="bg-white p-3 rounded border border-yellow-200">
                  <p className="leading-relaxed">AI analysis is supplementary. For complete verification, ensure certificates are uploaded by authorized institutions to our database.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 flex items-center justify-between">
            <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold shadow-sm ${
              isAuthentic ? 'bg-blue-600 text-white' : 'bg-red-600 text-white'
            }`}>
              🤖 AI ANALYSIS {auth.details?.isMockModel ? '(DEMO)' : ''}
            </span>
          </div>
        </div>
      );
    }

    // Error Result
    if (verificationResult.method === 'error' || verificationResult.method === 'ml_error') {
      return (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-center mb-4">
            <div className="flex-shrink-0">
              <svg className="h-8 w-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-lg font-medium text-red-800">❌ Verification Failed</h3>
              <p className="text-sm text-red-600">
                {verificationResult.method === 'ml_error' ? 'AI analysis failed' : 'Verification process failed'}
              </p>
            </div>
          </div>
          <div className="text-sm text-red-700">
            <p><span className="font-medium">Error:</span> {verificationResult.error || verificationResult.mlError}</p>
            <p className="mt-2">Please try again or contact support if the issue persists.</p>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 py-8 px-4">
      {/* Wave Background */}
      <div className="page-wave-background">
        <img src="/hero-wave.svg" alt="" className="wave-image" />
      </div>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-4">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Home
          </Link>
          <h1 className="text-4xl font-bold text-gray-800 mb-2">🔍 Certificate Verification</h1>
          <p className="text-lg text-gray-600">
            Upload a certificate to verify its authenticity through our institutional database and AI analysis
          </p>
        </div>

        {/* File Upload Section */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <div 
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors duration-200 ${
              dragActive ? 'border-blue-400 bg-blue-50' : 'border-gray-300 hover:border-blue-400'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <input
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="hidden"
              id="certificate-upload"
            />
            <label htmlFor="certificate-upload" className="cursor-pointer">
              <div className="mb-4">
                <svg className="h-12 w-12 text-gray-400 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
              </div>
              <p className="text-lg font-medium text-gray-700 mb-2">Upload Certificate</p>
              <p className="text-sm text-gray-500">Click to browse or drag and drop your certificate image</p>
            </label>
          </div>

          {file && (
            <div className="mt-4 p-3 bg-blue-50 rounded-lg">
              <p className="text-sm font-medium text-blue-800">📄 Selected: {file.name}</p>
              <p className="text-xs text-blue-600">Size: {(file.size / 1024 / 1024).toFixed(2)} MB</p>
            </div>
          )}

          <button
            onClick={handleVerify}
            disabled={!file || isHashing || isChecking}
            className="btn-navy w-max font-bold rounded-md focus:ring-4 text-sm px-4 md:px-6 py-2 lg:py-2.5 border border-[#09314D] bg-[#09314D] text-white hover:bg-white hover:text-[#09314D] hidden lg:block mt-4"
          >
            {isHashing ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0a12 12 0 100 24v-4a8 8 0 010-16z"></path>
                </svg>
                <span>Generating Hash...</span>
              </>
            ) : isChecking ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0a12 12 0 100 24v-4a8 8 0 010-16z"></path>
                </svg>
                <span>Verifying...</span>
              </>
            ) : (
              <span>Verify Certificate</span>
            )}
          </button>
        </div>

        {/* Progress Steps */}
        {currentStep > 0 && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-8">
            <div className="flex items-center justify-between mb-4">
              <div className={`flex items-center ${currentStep >= 1 ? 'text-blue-600' : 'text-gray-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>
                  {currentStep > 1 ? '✓' : '1'}
                </div>
                <span className="ml-2 font-medium">Database Check</span>
              </div>
              
              <div className={`flex items-center ${currentStep >= 3 ? 'text-blue-600' : 'text-gray-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep >= 3 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>
                  {currentStep > 3 ? '✓' : '2'}
                </div>
                <span className="ml-2 font-medium">AI Analysis</span>
              </div>
            </div>

            {checkingStep && (
              <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                <p className="text-sm font-medium text-gray-700">{checkingStep}</p>
                {isChecking && (
                  <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-blue-600 h-2 rounded-full animate-pulse" style={{ width: `${currentStep * 50}%` }}></div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Results Section */}
        {renderResults()}
      </div>
    </div>
  );
};

export default CheckPage;